import { Fragment, ReactNode } from "react";
import { isAxiosError } from "axios";
import { isNumeric } from "validator";

import { Reading } from "@/entities/Reading";
import { API_ERROR } from "@/shared/const/const";
import { ApiError } from "@/shared/types";

import { captureException } from "@sentry/react";

export const handleError = (error: any | undefined): ApiError => {
  const result: ApiError = {};

  if (typeof error === "undefined") {
    result.details = API_ERROR.DEFAULT;

    return result;
  }

  try {
    if (isAxiosError(error)) {
      result.code = error.code;

      const errorData = error.response?.data;

      if (errorData) {
        const detail = errorData.detail || errorData;

        if (typeof detail === "string") {
          result.details = detail;
        } else if (Array.isArray(detail)) {
          result.details = detail.join(" ");
        } else if (typeof detail === "object") {
          Object.entries(detail).forEach(([fieldName, messages]) => {
            // message without field name is most likely the default error message
            if (isNumeric(fieldName)) {
              result.details = String(messages);
            }

            result[fieldName] = Array.isArray(messages) ? messages.join(" ") : String(messages);
          });

          if (detail.nonFieldErrors) {
            result.details = Array.isArray(detail.nonFieldErrors)
              ? detail.nonFieldErrors.join(" ")
              : String(detail.nonFieldErrors);
          }
        }
      }
    } else {
      if (error.nonFieldErrors) {
        result.details = Array.isArray(error.nonFieldErrors)
          ? error.nonFieldErrors.join(" ")
          : String(error.nonFieldErrors);
      } else if (error.message) {
        result.details = error.message as string;
      }
    }
  } catch (err: any) {
    result.details = err?.message;
  }

  if (!result.details) {
    result.details = API_ERROR.DEFAULT;
  }

  return result;
};

function maskSensitive(obj: unknown) {
  if (Array.isArray(obj)) {
    return obj.map(maskSensitive);
  }

  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (["password", "card_number"].some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey))) {
          const str = value !== null ? String(value) : "";
          return [key, "*".repeat(str.length)];
        }
        return [key, maskSensitive(value)];
      })
    );
  }

  return obj;
}

export function safelyCaptureException(err: unknown) {
  if (isAxiosError(err)) {
    captureException(err, {
      extra: {
        message: err.message,
        code: err.code,
        url: err.config?.url,
        method: err.config?.method,
        data: maskSensitive(err.config?.data),
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        headers: err.response?.headers,
      },
      tags: {
        type: "AxiosError",
      },
    });
    return;
  }

  if (err instanceof Error) {
    captureException(err);
    return;
  }

  const wrappedError = new Error(typeof err === "string" ? err : "Unknown error");
  (wrappedError as unknown as Error & { originalError: unknown }).originalError = err;
  captureException(wrappedError);
}

export const replaceAsterisksWith = (value: string): ReactNode => {
  const regex = /\*\*(\w+)\*\*/g;

  const keywords = (value.match(regex) || []).map((el: string) => el.replace(/\*\*/g, ""));
  const results = value.split(regex);

  return results.map((result, i) => {
    if (keywords.includes(result)) {
      return <span key={i}>{result}</span>;
    }

    return <Fragment key={i}>{result}</Fragment>;
  });
};

export function getReadingStatus(reading: Reading): string {
  const prefix = [];
  if (reading.isRushed) {
    prefix.push("(RUSH)");
  }
  if (reading.numberOfTarotCards > 1) {
    prefix.push(`(${reading.numberOfTarotCards})`);
  }
  if (reading.isOracle) {
    prefix.push("(OR)");
  }
  if (reading.isFeelings) {
    prefix.push("(FE)");
  }
  return prefix.join("");
}
